import { useState, useEffect, useCallback } from 'react';
import { Bell, BellOff, BellRing, Check, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const NOTIFICATION_STORAGE_KEY = 'clubviajemos_notifications';
const REMINDED_EVENTS_KEY = 'clubviajemos_reminded_events';

// Check if browser supports notifications
const isNotificationSupported = () => {
  return 'Notification' in window;
};

// Get notification permission status
const getPermissionStatus = () => {
  if (!isNotificationSupported()) return 'unsupported';
  return Notification.permission;
};

// Request notification permission
const requestPermission = async () => {
  if (!isNotificationSupported()) return 'unsupported';
  
  try {
    const permission = await Notification.requestPermission();
    return permission;
  } catch (error) {
    return 'denied';
  }
};

// Show a browser notification
const showNotification = (title, options = {}) => {
  if (getPermissionStatus() !== 'granted') return;
  
  try {
    const notification = new Notification(title, {
      icon: '/favicon.svg',
      badge: '/favicon.svg',
      tag: options.tag || 'clubviajemos-event',
      requireInteraction: true,
      ...options
    });
    
    notification.onclick = () => {
      window.focus();
      if (options.onClick) options.onClick();
      notification.close();
    };
    
    return notification;
  } catch (e) {
    console.error('Failed to show notification:', e);
  }
};

// Get reminded events from localStorage
const getRemindedEvents = () => {
  try {
    const stored = localStorage.getItem(REMINDED_EVENTS_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

// Mark event as reminded
const markEventReminded = (eventId, reminderType) => {
  const reminded = getRemindedEvents();
  if (!reminded[eventId]) reminded[eventId] = {};
  reminded[eventId][reminderType] = true;
  localStorage.setItem(REMINDED_EVENTS_KEY, JSON.stringify(reminded));
};

// Check if event was already reminded
const wasEventReminded = (eventId, reminderType) => {
  const reminded = getRemindedEvents();
  return reminded[eventId]?.[reminderType] === true;
};

// Format time remaining for notification
const formatTimeForNotification = (hours) => {
  if (hours >= 24) {
    const days = Math.floor(hours / 24);
    return days === 1 ? 'mañana' : `en ${days} días`;
  } else if (hours >= 1) {
    return hours === 1 ? 'en 1 hora' : `en ${hours} horas`;
  } else {
    return 'muy pronto';
  }
};

// Hook for managing event notifications
export const useEventNotifications = (events) => {
  const [permissionStatus, setPermissionStatus] = useState(getPermissionStatus());
  const [notificationsEnabled, setNotificationsEnabled] = useState(() => {
    try {
      return localStorage.getItem(NOTIFICATION_STORAGE_KEY) === 'enabled';
    } catch {
      return false;
    }
  });

  // Update permission status when it changes
  useEffect(() => {
    setPermissionStatus(getPermissionStatus());
  }, []);

  // Request permission and enable notifications
  const enableNotifications = useCallback(async () => {
    const permission = await requestPermission();
    setPermissionStatus(permission);
    
    if (permission === 'granted') {
      setNotificationsEnabled(true);
      localStorage.setItem(NOTIFICATION_STORAGE_KEY, 'enabled');
      toast.success('Notificaciones activadas', {
        description: 'Recibirás recordatorios antes de cada evento'
      });
      
      // Show test notification
      showNotification('Club Viajemos - Notificaciones Activas', {
        body: 'Te avisaremos 24 horas y 1 hora antes de cada evento.',
        tag: 'test-notification'
      });
    } else if (permission === 'denied') {
      toast.error('Notificaciones bloqueadas', {
        description: 'Habilítalas en la configuración de tu navegador'
      });
    }
  }, []);

  // Disable notifications
  const disableNotifications = useCallback(() => {
    setNotificationsEnabled(false);
    localStorage.setItem(NOTIFICATION_STORAGE_KEY, 'disabled');
    toast.info('Notificaciones desactivadas');
  }, []);

  // Check and send reminders for upcoming events
  useEffect(() => {
    if (!notificationsEnabled || permissionStatus !== 'granted' || !events?.length) {
      return;
    }

    const checkReminders = () => {
      const now = new Date();
      
      events.forEach(event => {
        const eventDateTime = new Date(`${event.event_date}T${event.event_time}:00`);
        const hoursUntilEvent = (eventDateTime - now) / (1000 * 60 * 60);
        
        // 24 hour reminder (between 23-25 hours before)
        if (hoursUntilEvent > 23 && hoursUntilEvent <= 25) {
          if (!wasEventReminded(event.id, '24h')) {
            showNotification(`Evento mañana: ${event.title}`, {
              body: `${event.description || 'No te lo pierdas'}\n\nComienza ${formatTimeForNotification(24)}`,
              tag: `event-24h-${event.id}`,
              onClick: () => {
                window.open(event.zoom_link, '_blank');
              }
            });
            markEventReminded(event.id, '24h');
          }
        }
        
        // 1 hour reminder (between 50 min - 70 min before)
        if (hoursUntilEvent > 0.83 && hoursUntilEvent <= 1.17) {
          if (!wasEventReminded(event.id, '1h')) {
            showNotification(`Evento en 1 hora: ${event.title}`, {
              body: `¡Prepárate! El evento comienza pronto.\n\nClick para unirte a Zoom`,
              tag: `event-1h-${event.id}`,
              onClick: () => {
                window.open(event.zoom_link, '_blank');
              }
            });
            markEventReminded(event.id, '1h');
          }
        }
        
        // Event starting now (0-5 min before)
        if (hoursUntilEvent > 0 && hoursUntilEvent <= 0.083) {
          if (!wasEventReminded(event.id, 'now')) {
            showNotification(`¡AHORA! ${event.title}`, {
              body: `El evento está comenzando. Click para unirte.`,
              tag: `event-now-${event.id}`,
              onClick: () => {
                window.open(event.zoom_link, '_blank');
              }
            });
            markEventReminded(event.id, 'now');
          }
        }
      });
    };

    // Check immediately
    checkReminders();
    
    // Check every minute
    const interval = setInterval(checkReminders, 60 * 1000);
    
    return () => clearInterval(interval);
  }, [events, notificationsEnabled, permissionStatus]);

  return {
    permissionStatus,
    notificationsEnabled,
    enableNotifications,
    disableNotifications,
    isSupported: isNotificationSupported()
  };
};

// Notification toggle button component
export const NotificationToggle = ({ events }) => {
  const {
    permissionStatus,
    notificationsEnabled,
    enableNotifications,
    disableNotifications,
    isSupported
  } = useEventNotifications(events);

  if (!isSupported) {
    return null; // Don't show button if notifications not supported
  }

  const isEnabled = notificationsEnabled && permissionStatus === 'granted';

  return (
    <Button
      data-testid="notification-toggle"
      variant={isEnabled ? "default" : "outline"}
      size="sm"
      onClick={isEnabled ? disableNotifications : enableNotifications}
      className={`gap-2 ${
        isEnabled 
          ? 'bg-[#80C753] hover:bg-[#6ab043] text-white' 
          : 'border-[#45A0EE] text-[#45A0EE] hover:bg-[#45A0EE]/10'
      }`}
    >
      {isEnabled ? (
        <>
          <BellRing className="h-4 w-4" />
          <span className="hidden sm:inline">Recordatorios activos</span>
          <Check className="h-3 w-3" />
        </>
      ) : (
        <>
          <Bell className="h-4 w-4" />
          <span className="hidden sm:inline">Activar recordatorios</span>
        </>
      )}
    </Button>
  );
};

export default NotificationToggle;
